/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IRDProgramProfile } from "Interfaces/RD/ProgramProfile/IRDProgramProfile";
import { IRDUserProfile } from "Interfaces/RD/UserProfile/IRDUserProfile";
import { IRDUserProfileDetail } from "Interfaces/RD/UserProfile/IRDUserProfileDetail";
import { IUserProfileComment } from "Interfaces/UserProfile/IUserProfileComment";

import {
  RD_ADD_AZURE_BENEFIT_DETAIL,
  RD_ADD_AZURE_BENEFIT_DETAIL_FAIL,
  RD_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  RD_ADD_AZURE_BENEFIT_RESET,
  RD_ADD_USER_PROFILE_COMMENT,
  RD_ADD_USER_PROFILE_COMMENT_FAIL,
  RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES,
  RD_ADD_USER_PROFILE_COMMENT_SUCCESS,
  RD_DOWNLOAD_TO_CSV,
  RD_DOWNLOAD_TO_CSV_FAIL,
  RD_DOWNLOAD_TO_CSV_SUCCESS,
  RD_EXTEND_AZURE_BENEFIT_CREDIT,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
  RD_FETCH_PROGRAM_PROFILE,
  RD_FETCH_PROGRAM_PROFILE_FAIL,
  RD_FETCH_PROGRAM_PROFILE_SUCCESS,
  RD_FETCH_USER_PROFILE,
  RD_FETCH_USER_PROFILE_COMMENTS,
  RD_FETCH_USER_PROFILE_COMMENTS_FAIL,
  RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS,
  RD_FETCH_USER_PROFILE_FAIL,
  RD_FETCH_USER_PROFILE_SUCCESS,
  RD_GET_M365_BENEFIT_DETAIL,
  RD_GET_M365_BENEFIT_DETAIL_FAILURE,
  RD_GET_M365_BENEFIT_DETAIL_SUCCESS,
  RD_GET_MS365_BENEFIT_DETAIL,
  RD_GET_MS365_BENEFIT_DETAIL_FAILURE,
  RD_GET_MS365_BENEFIT_DETAIL_SUCCESS,
  RD_GET_PROFILE_BENEFITS,
  RD_GET_PROFILE_BENEFITS_FAIL,
  RD_GET_PROFILE_BENEFITS_SUCCESS,
  RD_GET_TEAMS_BENEFIT_DETAIL,
  RD_GET_TEAMS_BENEFIT_DETAIL_FAILURE,
  RD_GET_TEAMS_BENEFIT_DETAIL_SUCCESS,
  RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS,
  RD_RESET_FILE_DOWNLOAD_STATUS,
  RD_RESET_M365_BENEFIT_DETAIL,
  RD_RESET_MS365_BENEFIT_DETAIL,
  RD_UPDATE_LISTING_CURRENT_PAGE,
  RD_UPDATE_LISTING_PAGE_SIZE,
  RD_UPDATE_LISTING_SORTING,
  RD_UPDATE_M365_BENEFIT_DETAIL,
  RD_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
  RD_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
  RD_UPDATE_MS365_BENEFIT_DETAIL,
  RD_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
  RD_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
  RD_UPDATE_PROGRAM_PROFILE,
  RD_UPDATE_PROGRAM_PROFILE_FAIL,
  RD_UPDATE_PROGRAM_PROFILE_SUCCESS,
  RD_UPDATE_USER_PROFILE_ID,
  RD_USER_PROFILE_CLAIM_BENEFIT,
  RD_USER_PROFILE_CLAIM_BENEFIT_FAIL,
  RD_USER_PROFILE_CLAIM_BENEFIT_RESET,
  RD_USER_PROFILE_CLAIM_BENEFIT_SUCCESS,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  RD_USER_PROFILES_FILTER_VALUE_ADD,
  RD_USER_PROFILES_FILTER_VALUE_REMOVE,
  RD_USER_PROFILES_FILTER_VALUE_RESET,
  RD_USER_PROFILES_FILTER_VALUE_UPDATE,
  RD_USER_PROFILES_SEARCH,
  RD_USER_PROFILES_SEARCH_FAIL,
  RD_USER_PROFILES_SEARCH_SUCCESS,
  RD_USERPROFILE_UPDATE_IS_PRIVATE,
  RD_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  RD_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
} from "./RDUserProfileActionTypes";
import { ITeamsBenefitDetail } from "Interfaces/Benefits/ITeamsBenefitDetail";
import { IRDAzureBenefitClaim } from "Interfaces/RD/IRDAzureBenefitClaim";

/*
 * Action to Reset the M365 Benefit Detail State.
 * @interface
 */
export interface IRDResetM365BenefitDetail {
  type: typeof RD_RESET_M365_BENEFIT_DETAIL;
}

/*
 * Action to get the users M365 Details.
 * @interface
 */
export interface IRDFetchM365BenefitDetail {
  type: typeof RD_GET_M365_BENEFIT_DETAIL;
  userProfileId: number;
}

/*
 * Action to get the success fetch details.
 * @interface
 */
export interface IRDFetchM365BenefitDetailSuccess {
  type: typeof RD_GET_M365_BENEFIT_DETAIL_SUCCESS;
  m365BenefitDetail: IM365BenefitDetail;
}

/*
 * Action to get the failure fetch details.
 * @interface
 */
export interface IRDFetchM365BenefitDetailFailure {
  type: typeof RD_GET_M365_BENEFIT_DETAIL_FAILURE;
  m365FetchErrorMessage: string;
}

/*
 * Action to get the users Teams Details.
 * @interface
 */
export interface IRDFetchTeamsBenefitDetail {
  type: typeof RD_GET_TEAMS_BENEFIT_DETAIL;
  userProfileId: number;
}

/*
 * Action to get the success fetch details.
 * @interface
 */
export interface IRDFetchTeamsBenefitDetailSuccess {
  type: typeof RD_GET_TEAMS_BENEFIT_DETAIL_SUCCESS;
  teamsBenefitDetail: ITeamsBenefitDetail;
}

/*
 * Action to get the failure fetch details.
 * @interface
 */
export interface IRDFetchTeamsBenefitDetailFailure {
  type: typeof RD_GET_TEAMS_BENEFIT_DETAIL_FAILURE;
  teamsFetchErrorMessage: string;
}

/*
 * Action to get the update details.
 * @interface
 */
export interface IRDUpdateM365BenefitDetail {
  type: typeof RD_UPDATE_M365_BENEFIT_DETAIL;
  m365BenefitDetail: IM365BenefitDetail;
}

/*
 * Action to get the success details.
 * @interface
 */
export interface IRDUpdateM365BenefitDetailSuccess {
  type: typeof RD_UPDATE_M365_BENEFIT_DETAIL_SUCCESS;
  m365BenefitDetail: IM365BenefitDetail;
}

/*
 * Action to get the failure details.
 * @interface
 */
export interface IRDUpdateM365BenefitDetailFailure {
  type: typeof RD_UPDATE_M365_BENEFIT_DETAIL_FAILURE;
  m365UpdateErrorMessage: string;
}

/* 976959
 * Action to Reset the Microsoft 365 Benefit Detail State.
 * @interface
 */
export interface IRDResetMS365BenefitDetail {
  type: typeof RD_RESET_MS365_BENEFIT_DETAIL;
}

/**
 * Action to get the users Microsoft 365 Benefit Detail.
 * @interface
 */
export interface IRDFetchMS365BenefitDetail {
  type: typeof RD_GET_MS365_BENEFIT_DETAIL;
  userProfileId: number;
}

/*
 * Action to get the success fetch details.
 * @interface
 */
export interface IRDFetchMS365BenefitDetailSuccess {
  type: typeof RD_GET_MS365_BENEFIT_DETAIL_SUCCESS;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/*
 * Action to get the failure fetch details.
 * @interface
 */
export interface IRDFetchMS365BenefitDetailFailure {
  type: typeof RD_GET_MS365_BENEFIT_DETAIL_FAILURE;
  ms365FetchErrorMessage: string;
}

/*
 * Action to get the update details.
 * @interface
 */
export interface IRDUpdateMS365BenefitDetail {
  type: typeof RD_UPDATE_MS365_BENEFIT_DETAIL;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/*
 * Action to get the success details.
 * @interface
 */
export interface IRDUpdateMS365BenefitDetailSuccess {
  type: typeof RD_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS;
  ms365BenefitDetail: IMicrosoft365BenefitDetail;
}

/*
 * Action to get the failure details.
 * @interface
 */
export interface IRDUpdateMS365BenefitDetailFailure {
  type: typeof RD_UPDATE_MS365_BENEFIT_DETAIL_FAILURE;
  ms365UpdateErrorMessage: string;
}

/**
 * Action to get Extend Azure Benefit Credit.
 * @interface
 */
export interface IRDExtendAzureBenefitCredit {
  type: typeof RD_EXTEND_AZURE_BENEFIT_CREDIT;
  credit: number;
  userProfileId: number;
}

/**
 * Action to get the update success details.
 * @interface
 */
export interface IRDExtendAzureBenefitCreditSuccess {
  type: typeof RD_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS;
  capCredit: number;
}

/**
 * Action to get the update failure details.
 * @interface
 */
export interface IRDExtendAzureBenefitCreditFailure {
  type: typeof RD_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE;
  error: string;
}

/**
 * Action to reset Extend Azure Benefit Credit State.
 */
export interface IRDExtendAzureBenefitCreditReset {
  type: typeof RD_EXTEND_AZURE_BENEFIT_CREDIT_RESET;
}

/*
 * Action to get profile benefits.
 * @interface
 */
export interface IRDGetProfileBenefits {
  type: typeof RD_GET_PROFILE_BENEFITS;
  userProfileId: number;
}

/**
 * Action to get profile benefits success.
 * @interface
 */
export interface IRDGetProfileBenefitsSuccess {
  type: typeof RD_GET_PROFILE_BENEFITS_SUCCESS;
  userProfileBenefits: IProfileBenefitResult;
}

/**
 * Action to get profile benefits fail.
 * @interface
 */
export interface IRDGetProfileBenefitsFail {
  type: typeof RD_GET_PROFILE_BENEFITS_FAIL;
  profileBenefitsErrorMessage: string;
}

/**
 * Action to Reset values
 * @interface
 */
export interface IRDAddUserProfileCommentResetValues {
  type: typeof RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES;
}

/**
 * Action to update selected user profile id.
 */
export interface IRDUpdateUserProfileId {
  type: typeof RD_UPDATE_USER_PROFILE_ID;
  selectedUserProfileId: number;
}

/**
 * Action to Reset values
 * @interface
 */
export interface IRDProgramProfileUpdateResetStatus {
  type: typeof RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS;
}

/**
 * Action to update program profile.
 * @interface
 */
export interface IRDUpdateProgramProfile {
  type: typeof RD_UPDATE_PROGRAM_PROFILE;
  rdUserProfileId: number;
  programProfileStatus: string;
  modifiedBy: string;
}

/**
 * Action to update program profile success.
 * @interface
 */
export interface IRDUpdateProgramProfileSuccess {
  type: typeof RD_UPDATE_PROGRAM_PROFILE_SUCCESS;
  savedProgramProfileStatusName: string;
}

/**
 * Action to update program profile fail.
 * @interface
 */
export interface IRDUpdateProgramProfileFail {
  type: typeof RD_UPDATE_PROGRAM_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to add User Profile comment
 * @interface
 */
export interface IRDAddUserProfileComment {
  type: typeof RD_ADD_USER_PROFILE_COMMENT;
  rdUserProfileId: number;
  textValue: string;
  createdBy: string;
}

/**
 * Action to add User Profile comment success
 * @interface
 */
export interface IRDAddUserProfileCommentSuccess {
  type: typeof RD_ADD_USER_PROFILE_COMMENT_SUCCESS;
  rdUserProfileId: number;
}

/**
 * Action to add User Profile comment fail
 * @interface
 */
export interface IRDAddUserProfileCommentFail {
  type: typeof RD_ADD_USER_PROFILE_COMMENT_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch User Profile comments
 * @interface
 */
export interface IRDFetchUserProfileComments {
  type: typeof RD_FETCH_USER_PROFILE_COMMENTS;
  rdUserProfileId: number;
}

/**
 * Action to fetch User Profile comments success
 * @interface
 */
export interface IRDFetchUserProfileCommentsSuccess {
  type: typeof RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS;
  userProfileComments: IUserProfileComment[];
}

/**
 * Action to fetch User Profile comments fail
 * @interface
 */
export interface IRDFetchUserProfileCommentsFail {
  type: typeof RD_FETCH_USER_PROFILE_COMMENTS_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch User Profile
 * @interface
 */
export interface IRDFetchUserProfile {
  type: typeof RD_FETCH_USER_PROFILE;
  rdUserProfileId: number;
}

/**
 * Action to fetch User Profile Fail
 * @interface
 */
export interface IRDFetchUserProfileFail {
  type: typeof RD_FETCH_USER_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action to fetch User Profile Success
 * @interface
 */
export interface IRDFetchUserProfileSuccess {
  type: typeof RD_FETCH_USER_PROFILE_SUCCESS;
  rdUserProfile: IRDUserProfileDetail;
}

/**
 * Action for when downloading results to CSV
 * @interface
 */
export interface IRDDownloadToCSV {
  type: typeof RD_DOWNLOAD_TO_CSV;
  searchText: string;
  selectedUserProfilesFilterValues: IFilterValue[];
}

/**
 * Action for when downloading results to CSV success
 * @interface
 */
export interface IRDDownloadToCSVSuccess {
  type: typeof RD_DOWNLOAD_TO_CSV_SUCCESS;
  csvFile: string;
}

/**
 * Action for when downloading results to CSV failed
 * @interface
 */
export interface IRDDownloadToCSVFail {
  type: typeof RD_DOWNLOAD_TO_CSV_FAIL;
  errorMessage: string;
}

/**
 * Action for when updating listing current page
 * @interface
 */
export interface IRDUpdateCurrentPage {
  type: typeof RD_UPDATE_LISTING_CURRENT_PAGE;
  currentPage: number;
}

/**
 * Action for when updating listing page size
 * @interface
 */
export interface IRDUpdatePageSize {
  type: typeof RD_UPDATE_LISTING_PAGE_SIZE;
  pageSize: number;
}

/**
 * Action to update azure Benefit Detail.
 * @interface
 */
export interface IRDAddAzureBenefitDetail {
  type: typeof RD_ADD_AZURE_BENEFIT_DETAIL;
  azureBenefit: IRDAzureBenefitClaim;
  userProfileId: number;
}

/**
 * Action to update azure Benefit Detail success.
 * @interface
 */
export interface IRDAddAzureBenefitDetailSuccess {
  type: typeof RD_ADD_AZURE_BENEFIT_DETAIL_SUCCESS;
}

/**
 * Action to update azure Benefit Detail fail.
 * @interface
 */
export interface IRDAddAzureBenefitDetailFail {
  type: typeof RD_ADD_AZURE_BENEFIT_DETAIL_FAIL;
  errorMessage: string;
}

/**
 * Action to update azure Benefit Detail Reset.
 * @interface
 */
export interface IRDAddAzureBenefitReset {
  type: typeof RD_ADD_AZURE_BENEFIT_RESET;
}

/**
 * Action for when updating listing sorting
 * @interface
 */
export interface IRDUpdateSorting {
  type: typeof RD_UPDATE_LISTING_SORTING;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action for when reset file download status
 * @interface
 */
export interface IRDResetFileDownloadStatus {
  type: typeof RD_RESET_FILE_DOWNLOAD_STATUS;
}

/**
 * Action for when searching rd profiles.
 * @interface
 */
export interface IRDProfilesSearch {
  type: typeof RD_USER_PROFILES_SEARCH;
  searchText: string;
  selectedUserProfilesFilterValues: IFilterValue[];
  searchPage: number;
  pageSize: number;
  sortColumn?: string;
  sortDescending?: boolean;
}

/**
 * Action for when user profiles were searched successfully.
 * @interface
 */
export interface IRDProfilesSuccess {
  type: typeof RD_USER_PROFILES_SEARCH_SUCCESS;
  userProfiles: IRDUserProfile[];
  searchPage: number;
  filteredCount: number;
  pageSize: number;
}

/**
 * Action for when searching user profiles failed.
 * @interface
 */
export interface IRDProfilesFail {
  type: typeof RD_USER_PROFILES_SEARCH_FAIL;
  error: string;
}

/**
 * Action for when adding selected filter value.
 * @interface
 */
export interface IRDUserProfilesFilterValueAdd {
  type: typeof RD_USER_PROFILES_FILTER_VALUE_ADD;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected filter value.
 * @interface
 */
export interface IRDUserProfilesFilterValueRemove {
  type: typeof RD_USER_PROFILES_FILTER_VALUE_REMOVE;
  filterValue: IFilterValue;
}

/**
 * Action for when removing selected province filter value.
 * @interface
 */
export interface IRDUserProfilesFilterProvinceValueRemove {
  type: typeof RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE;
}

/**
 * Action for when updating selected filter value.
 * @interface
 */
export interface IRDUserProfilesFilterValueUpdate {
  type: typeof RD_USER_PROFILES_FILTER_VALUE_UPDATE;
  filterValue: IFilterValue;
}

/**
 * Action claiming the benefits for a user profile.
 * @interface
 */
export interface IRDClaimBenefit {
  type: typeof RD_USER_PROFILE_CLAIM_BENEFIT;
  userProfileId: number;
  benefitType: string;
}

/**
 * Action claiming the benefits for a user profile succeed.
 * @interface
 */
export interface IRDClaimBenefitSuccess {
  type: typeof RD_USER_PROFILE_CLAIM_BENEFIT_SUCCESS;
}

/**
 * Action claiming the benefits for a user profile fail.
 * @interface
 */
export interface IRDClaimBenefitFail {
  type: typeof RD_USER_PROFILE_CLAIM_BENEFIT_FAIL;
  error: string;
}

/**
 * Action to fetch Program Profile comments
 * @interface
 */
export interface IRDFetchProgramProfile {
  type: typeof RD_FETCH_PROGRAM_PROFILE;
  programProfileId: number;
}

/**
 * Action to fetch Program Profile success
 * @interface
 */
export interface IRDFetchProgramProfileSuccess {
  type: typeof RD_FETCH_PROGRAM_PROFILE_SUCCESS;
  programProfile: IRDProgramProfile;
}

/**
 * Action to fetch Program Profile fail
 * @interface
 */
export interface IRDFetchProgramProfileFail {
  type: typeof RD_FETCH_PROGRAM_PROFILE_FAIL;
  errorMessage: string;
}

/**
 * Action claiming the benefits for a user profile reset.
 * @interface
 */
export interface IRDClaimBenefitReset {
  type: typeof RD_USER_PROFILE_CLAIM_BENEFIT_RESET;
}

/**
 * Action used for reseting the benefit details state.
 * @returns {IRDResetM365BenefditDetail} the action.
 */
export function m365BenefitDetailReset(): IRDResetM365BenefitDetail {
  return {
    type: RD_RESET_M365_BENEFIT_DETAIL,
  };
}

/**
 * Action: update user's profile is private flag.
 * @interface
 */
export interface IRDUserProfileIsPrivateUpdate {
  type: typeof RD_USERPROFILE_UPDATE_IS_PRIVATE;
  isPrivate: boolean;
  userProfileId: number;
}

/**
 * Action: update user's profile is private flag success.
 * @interface
 */
export interface IRDUserProfileIsPrivateUpdateSuccess {
  type: typeof RD_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS;
}

/**
 * Action: update user's profile is private flag fail.
 * @interface
 */
export interface IRDUserProfileIsPrivateUpdateFail {
  type: typeof RD_USERPROFILE_UPDATE_IS_PRIVATE_FAIL;
  error: string;
}

/**
 * Action: reset rd user profile filter value.
 * @interface
 */
export interface IRDUserProfileFilterValueReset {
  type: typeof RD_USER_PROFILES_FILTER_VALUE_RESET;
}

/**
 * Action: update user's profile primary email.
 * @interface
 */
export interface IRDUserProfilePrimaryEmailUpdate {
  type: typeof RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL;
  userProfileId: number;
  newEmail: string;
}

/**
 * Action: update user's profile primary email success.
 * @interface
 */
export interface IRDUserProfilePrimaryEmailUpdateSuccess {
  type: typeof RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS;
}

/**
 * Action: update user's profile primary email fail.
 * @interface
 */
export interface IRDUserProfilePrimaryEmailUpdateFail {
  type: typeof RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL;
  error: string;
}

/**
 * Action: reset user's profile primary email update.
 * @interface
 */
export interface IRDUserProfilePrimaryEmailUpdateReset {
  type: typeof RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET;
}

/**
 * Action used for M365 Fetch.
 * @returns {IRDFetchM365BenefitDetail} the action.
 */
export function fetchM365BenefitDetail(userProfileId: number): IRDFetchM365BenefitDetail {
  return {
    type: RD_GET_M365_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for M365 Fetch Success.
 * @returns {IRDFetchM365BenefitDetailSuccess} the action.
 */
export function fetchM365BenefitDetailSuccess(
  m365BenefitDetail: IM365BenefitDetail
): IRDFetchM365BenefitDetailSuccess {
  return {
    type: RD_GET_M365_BENEFIT_DETAIL_SUCCESS,
    m365BenefitDetail,
  };
}

/**
 * Action used for M365 Fetch Failure.
 * @returns {IRDFetchM365BenefitDetailFailure} the action.
 */
export function fetchM365BenefitDetailFailure(
  m365FetchErrorMessage: string
): IRDFetchM365BenefitDetailFailure {
  return {
    type: RD_GET_M365_BENEFIT_DETAIL_FAILURE,
    m365FetchErrorMessage,
  };
}

/**
 * Action used for updating the M365 Detail.
 * @returns {IRDUpdateM365BenefitDetail} the action.
 */
export function updateM365BenefitDetail(
  m365BenefitDetail: IM365BenefitDetail
): IRDUpdateM365BenefitDetail {
  return {
    type: RD_UPDATE_M365_BENEFIT_DETAIL,
    m365BenefitDetail,
  };
}

/**
 * Action used for update m365 benefit success.
 * @returns {IRDUpdateM365BenefitDetailSuccess} the action.
 */
export function updateM365BenefitDetailSuccess(
  m365BenefitDetail: IM365BenefitDetail
): IRDUpdateM365BenefitDetailSuccess {
  return {
    type: RD_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
    m365BenefitDetail,
  };
}

/**
 * Action used for update m365 failure.
 * @returns {IRDUpdateM365BenefitDetailFailure} the action.
 */
export function updateM365BenefitDetailFailure(
  m365UpdateErrorMessage: string
): IRDUpdateM365BenefitDetailFailure {
  return {
    type: RD_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
    m365UpdateErrorMessage,
  };
}

/**
 * Action used for resetting the state.
 * @returns {IRDResetMS365BenefitDetail} the action.
 */
export function ms365BenefitDetailReset(): IRDResetMS365BenefitDetail {
  return {
    type: RD_RESET_MS365_BENEFIT_DETAIL,
  };
}

/**
 * Action used for fetch MS365 Benefit Details.
 * @returns {IRDFetchMS365BenefitDetail} the action.
 */
export function fetchMS365BenefitDetail(userProfileId: number): IRDFetchMS365BenefitDetail {
  return {
    type: RD_GET_MS365_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for fetch success.
 * @returns {IRDFetchMS365BenefitDetailSuccess} the action.
 */
export function fetchMS365BenefitDetailSuccess(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IRDFetchMS365BenefitDetailSuccess {
  return {
    type: RD_GET_MS365_BENEFIT_DETAIL_SUCCESS,
    ms365BenefitDetail,
  };
}

/**
 * Action used for fetch failure.
 * @returns {IRDFetchMS365BenefitDetailFailure} the action.
 */
export function fetchMS365BenefitDetailFailure(
  ms365FetchErrorMessage: string
): IRDFetchMS365BenefitDetailFailure {
  return {
    type: RD_GET_MS365_BENEFIT_DETAIL_FAILURE,
    ms365FetchErrorMessage,
  };
}

/**
 * Action used for update details.
 * @returns {IRDUpdateMS365BenefitDetail} the action.
 */
export function updateMS365BenefitDetail(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IRDUpdateMS365BenefitDetail {
  return {
    type: RD_UPDATE_MS365_BENEFIT_DETAIL,
    ms365BenefitDetail,
  };
}

/**
 * Action used for update success.
 * @returns {IRDUpdateMS365BenefitDetailSuccess} the action.
 */
export function updateMS365BenefitDetailSuccess(
  ms365BenefitDetail: IMicrosoft365BenefitDetail
): IRDUpdateMS365BenefitDetailSuccess {
  return {
    type: RD_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
    ms365BenefitDetail,
  };
}

/**
 * Action used for update failure.
 * @returns {IRDUpdateMS365BenefitDetailFailure} the action.
 */
export function updateMS365BenefitDetailFailure(
  ms365UpdateErrorMessage: string
): IRDUpdateMS365BenefitDetailFailure {
  return {
    type: RD_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
    ms365UpdateErrorMessage,
  };
}

/**
 * Action used for update details.
 * @returns {IRDUpdateM365BenefitDetail} the action.
 */
export function extendAzureBenefitCredit(
  credit: number,
  userProfileId: number
): IRDExtendAzureBenefitCredit {
  return {
    type: RD_EXTEND_AZURE_BENEFIT_CREDIT,
    credit,
    userProfileId,
  };
}

/**
 * Action used for Teams Fetch.
 * @returns {IRDFetchTeamsBenefitDetail} the action.
 */
export function fetchTeamsBenefitDetail(userProfileId: number): IRDFetchTeamsBenefitDetail {
  return {
    type: RD_GET_TEAMS_BENEFIT_DETAIL,
    userProfileId,
  };
}

/**
 * Action used for Teams Fetch Success.
 * @returns {IRDFetchTeamsBenefitDetailSuccess} the action.
 */
export function fetchTeamsBenefitDetailSuccess(
  teamsBenefitDetail: ITeamsBenefitDetail
): IRDFetchTeamsBenefitDetailSuccess {
  return {
    type: RD_GET_TEAMS_BENEFIT_DETAIL_SUCCESS,
    teamsBenefitDetail,
  };
}

/**
 * Action used for Teams Fetch Failure.
 * @returns {IRDFetchTeamsBenefitDetailFailure} the action.
 */
export function fetchTeamsBenefitDetailFailure(
  teamsFetchErrorMessage: string
): IRDFetchTeamsBenefitDetailFailure {
  return {
    type: RD_GET_TEAMS_BENEFIT_DETAIL_FAILURE,
    teamsFetchErrorMessage,
  };
}

/**
 * Action used for update success.
 * @returns {IRDUpdateM365BenefitDetailSuccess} the action.
 */
export function extendAzureBenefitCreditSuccess(
  capCredit: number
): IRDExtendAzureBenefitCreditSuccess {
  return {
    type: RD_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
    capCredit,
  };
}

/**
 * Action used for update failure.
 * @returns {IRDUpdateM365BenefitDetailFailure} the action.
 */
export function extendAzureBenefitCreditFailure(error: string): IRDExtendAzureBenefitCreditFailure {
  return {
    type: RD_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
    error,
  };
}

export function extendAzureBenefitCreditReset(): IRDExtendAzureBenefitCreditReset {
  return {
    type: RD_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  };
}

/**
 * Action used for getting a profiles benefits.
 * @param userProfileId the userProfileId to get benefits for.
 * @returns {IRDGetProfileBenefits} the action.
 */
export function getProfileBenefits(userProfileId: number): IRDGetProfileBenefits {
  return {
    type: RD_GET_PROFILE_BENEFITS,
    userProfileId,
  };
}

/**
 * Action used for getting a profiles benefits success.
 * @param userProfileBenefits the benefits for the profile.
 * @returns {IRDGetProfileBenefitsSuccess} the action.
 */
export function getProfileBenefitsSuccess(
  userProfileBenefits: IProfileBenefitResult
): RDUserProfileActionTypes {
  return {
    type: RD_GET_PROFILE_BENEFITS_SUCCESS,
    userProfileBenefits,
  };
}

/**
 * Action used for getting a profiles benefits fail.
 * @param profileBenefitsErrorMessage the profileBenefitsErrorMessage to set.
 * @returns {IRDGetProfileBenefitsFail} the action.
 */
export function profileBenefitsFail(profileBenefitsErrorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_GET_PROFILE_BENEFITS_FAIL,
    profileBenefitsErrorMessage,
  };
}

/**
 * Action creator for UPDATE_LISTING_CURRENT_PAGE.
 */
export function updateRDCurrentPage(currentPage: number): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_LISTING_CURRENT_PAGE,
    currentPage,
  };
}

/**
 * Action creator for UPDATE_LISTING_PAGE_SIZE.
 */
export function updateRDPageSize(pageSize: number): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_LISTING_PAGE_SIZE,
    pageSize,
  };
}

/**
 * Action creator for UPDATE_LISTING_PAGE_SIZE.
 */
export function updateRDSorting(
  sortColumn?: string,
  sortDescending?: boolean
): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_LISTING_SORTING,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for RD_RESET_FILE_DOWNLOAD_STATUS.
 */
export function resetFileDownloadStatus(): RDUserProfileActionTypes {
  return {
    type: RD_RESET_FILE_DOWNLOAD_STATUS,
  };
}

/**
 * Action creator for RD_DOWNLOAD_TO_CSV.
 */
export function downloadRDUserProfilesToCSV(
  searchText: string,
  selectedUserProfilesFilterValues: IFilterValue[]
): RDUserProfileActionTypes {
  return {
    type: RD_DOWNLOAD_TO_CSV,
    searchText,
    selectedUserProfilesFilterValues,
  };
}

/**
 * Action creator for RD_DOWNLOAD_TO_CSV_SUCCESS.
 */
export function downloadRDUserProfilesToCSVSuccess(csvFile: string): RDUserProfileActionTypes {
  return {
    type: RD_DOWNLOAD_TO_CSV_SUCCESS,
    csvFile,
  };
}

/**
 * Action creator for RD_DOWNLOAD_TO_CSV_FAIL.
 */
export function downloadRDUserProfilesToCSVFail(errorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_DOWNLOAD_TO_CSV_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for RD_USER_PROFILES_SEARCH.
 */
export function searchRDUserProfiles(
  searchText: string,
  selectedUserProfilesFilterValues: IFilterValue[],
  searchPage: number,
  pageSize: number,
  sortColumn?: string,
  sortDescending?: boolean
): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_SEARCH,
    searchText,
    selectedUserProfilesFilterValues,
    searchPage,
    pageSize,
    sortColumn,
    sortDescending,
  };
}

/**
 * Action creator for RD_USER_PROFILES_SEARCH_SUCCESS.
 * @param userProfiles the user profiles successfully searched.
 */
export function searchUserRDProfilesSuccess(
  userProfiles: IRDUserProfile[],
  searchPage: number,
  pageSize: number,
  filteredCount: number
): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_SEARCH_SUCCESS,
    userProfiles,
    searchPage,
    filteredCount,
    pageSize,
  };
}

/**
 * Action creator for RD_USER_PROFILES_SEARCH_FAIL.
 * @param error the error message from the API.
 */
export function searchRDUserProfilesFail(error: string): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_SEARCH_FAIL,
    error,
  };
}

/**
 * Action creator for RD_USER_PROFILES_FILTER_VALUE_ADD.
 * @param error the error message from the API.
 */
export function addUserProfilesFilterValue(filterValue: IFilterValue): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_FILTER_VALUE_ADD,
    filterValue,
  };
}

/**
 * Action creator for RD_USER_PROFILES_FILTER_VALUE_REMOVE.
 * @param error the error message from the API.
 */
export function removeUserProfilesFilterValue(filterValue: IFilterValue): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_FILTER_VALUE_REMOVE,
    filterValue,
  };
}

/**
 * Action creator for RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE.
 */
export function removeUserProfilesFilterProvinceValue(): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  };
}

/**
 * Action creator for RD_USER_PROFILES_FILTER_VALUE_UPDATE.
 * @param error the error message from the API.
 */
export function updateUserProfilesFilterValue(filterValue: IFilterValue): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILES_FILTER_VALUE_UPDATE,
    filterValue,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE.
 * @param rdUserProfileId The user profile id to fetch.
 */
export function fetchUserProfile(rdUserProfileId: number): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE,
    rdUserProfileId,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE_SUCCESS.
 * @param rdUserProfile The user profile.
 */
export function fetchUserProfileSuccess(
  rdUserProfile: IRDUserProfileDetail
): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE_SUCCESS,
    rdUserProfile,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchUserProfileFail(errorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for UPDATE_USER_PROFILE_ID.
 */
export function updateSelectedUserProfileId(
  selectedUserProfileId: number
): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_USER_PROFILE_ID,
    selectedUserProfileId,
  };
}

/**
 * Action creator for RD_UPDATE_PROGRAM_PROFILE.
 * @param rdUserProfileId The user profile id.
 * @param programProfileStatus Program Profile name.
 * @param modifiedBy Admin user who modified program profile.
 */
export function updateRDProgramProfile(
  rdUserProfileId: number,
  programProfileStatus: string,
  modifiedBy: string
): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_PROGRAM_PROFILE,
    rdUserProfileId,
    programProfileStatus,
    modifiedBy,
  };
}

/**
 * Action creator for RD_UPDATE_PROGRAM_PROFILE_SUCCESS.
 * @param savedProgramProfileStatusName The saved program profile status name.
 */
export function updateRDProgramProfileSuccess(
  savedProgramProfileStatusName: string
): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_PROGRAM_PROFILE_SUCCESS,
    savedProgramProfileStatusName,
  };
}

/**
 * Action creator for RD_UPDATE_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function updateRDProgramProfileFail(errorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_UPDATE_PROGRAM_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for RD_FETCH_PROGRAM_PROFILE.
 * @param programProfileId The program profile id.
 */
export function fetchProgramProfile(programProfileId: number): IRDFetchProgramProfile {
  return {
    type: RD_FETCH_PROGRAM_PROFILE,
    programProfileId,
  };
}

/**
 * Action creator for RD_FETCH_PROGRAM_PROFILE_SUCCESS.
 * @param userProfile The user profile.
 */
export function fetchProgramProfileSuccess(
  programProfile: IRDProgramProfile
): IRDFetchProgramProfileSuccess {
  return {
    type: RD_FETCH_PROGRAM_PROFILE_SUCCESS,
    programProfile,
  };
}

/**
 * Action creator for RD_FETCH_PROGRAM_PROFILE_FAIL.
 * @param errorMessage The error message.
 */
export function fetchProgramProfileFail(errorMessage: string): IRDFetchProgramProfileFail {
  return {
    type: RD_FETCH_PROGRAM_PROFILE_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for RD_ADD_USER_PROFILE_COMMENT.
 * @param rdUserProfileId The user profile id.
 * @param textValue Text value.
 * @param createdBy Admin user who created comment.
 */
export function addUserProfileComment(
  rdUserProfileId: number,
  textValue: string,
  createdBy: string
): RDUserProfileActionTypes {
  return {
    type: RD_ADD_USER_PROFILE_COMMENT,
    rdUserProfileId,
    textValue,
    createdBy,
  };
}

/**
 * Action creator for RD_ADD_USER_PROFILE_COMMENT_SUCCESS.
 * @param rdUserProfileId The user profile id.
 */
export function addUserProfileCommentSuccess(rdUserProfileId: number): RDUserProfileActionTypes {
  return {
    type: RD_ADD_USER_PROFILE_COMMENT_SUCCESS,
    rdUserProfileId,
  };
}

/**
 * Action creator for RD_ADD_USER_PROFILE_COMMENT_FAIL.
 * @param errorMessage The error message.
 */
export function addUserProfileCommentFail(errorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_ADD_USER_PROFILE_COMMENT_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES.
 */
export function resetAddUserProfileCommentValues(): RDUserProfileActionTypes {
  return {
    type: RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES,
  };
}

/**
 * Action creator for RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS.
 */
export function resetUpdateProgramProfileUpdateStatus(): RDUserProfileActionTypes {
  return {
    type: RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE_COMMENTS.
 * @param rdUserProfileId The user profile id.
 */
export function fetchUserProfileComments(rdUserProfileId: number): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE_COMMENTS,
    rdUserProfileId,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS.
 * @param userProfileComments The user profile comments.
 */
export function fetchUserProfileCommentsSuccess(
  userProfileComments: IUserProfileComment[]
): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS,
    userProfileComments,
  };
}

/**
 * Action creator for RD_FETCH_USER_PROFILE_COMMENTS_FAIL.
 * @param errorMessage The error message.
 */
export function fetchUserProfileCommentsFail(errorMessage: string): RDUserProfileActionTypes {
  return {
    type: RD_FETCH_USER_PROFILE_COMMENTS_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for RD_USERPROFILE_CLAIM_BENEFIT.
 * @param {string} benefitType The selected benefit type to claim.
 * @param {number} userProfileId The user profile id to claim the benefit for.
 * @returns {RDUserProfileActionTypes}
 */
export function claimRdBenefit(benefitType: string, userProfileId: number): IRDClaimBenefit {
  return {
    type: RD_USER_PROFILE_CLAIM_BENEFIT,
    benefitType,
    userProfileId,
  };
}

/**
 * Action creator for RD_USERPROFILE_CLAIM_BENEFIT_SUCCESS.
 * @returns {RDUserProfileActionTypes}
 */
export function claimRdBenefitSuccess(): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILE_CLAIM_BENEFIT_SUCCESS,
  };
}

/**
 * Action creator for RD_USERPROFILE_CLAIM_BENEFIT_FAIL.
 * @param {string} error The error message from the API.
 * @returns {RDUserProfileActionTypes}
 */
export function claimRdBenefitFail(error: string): RDUserProfileActionTypes {
  return {
    type: RD_USER_PROFILE_CLAIM_BENEFIT_FAIL,
    error,
  };
}

/**
 * Action creator for USERPROFILE_CLAIM_BENEFIT_RESET.
 * @returns {RDUserProfileActionTypes}
 */
export function claimRdBenefitReset(): IRDClaimBenefitReset {
  return {
    type: RD_USER_PROFILE_CLAIM_BENEFIT_RESET,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE.
 * @returns {IRDUserProfileIsPrivateUpdate}
 */
export function userProfileIsPrivateUpdate(
  isPrivate: boolean,
  userProfileId: number
): IRDUserProfileIsPrivateUpdate {
  return {
    type: RD_USERPROFILE_UPDATE_IS_PRIVATE,
    isPrivate,
    userProfileId,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS.
 * @returns {IRDUserProfileIsPrivateUpdateSuccess}
 */
export function userProfileIsPrivateUpdateSuccess(): IRDUserProfileIsPrivateUpdateSuccess {
  return {
    type: RD_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  };
}

/**
 * Action creator for USERPROFILE_UPDATE_IS_PRIVATE_FAIL.
 * @returns {IRDUserProfileIsPrivateUpdateFail}
 */
export function userProfileIsPrivateUpdateFail(error: string): IRDUserProfileIsPrivateUpdateFail {
  return {
    type: RD_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
    error,
  };
}

/**
 * Action creator for USER_PROFILES_FILTER_VALUE_RESET.
 * @returns {IRDUserProfileFilterValueReset}
 */
export function userProfileFilterValueReset(): IRDUserProfileFilterValueReset {
  return {
    type: RD_USER_PROFILES_FILTER_VALUE_RESET,
  };
}

/**
 * Action creator for USER_PROFILE_UPDATE_PRIMARY_EMAIL.
 * @returns {IRDUserProfilePrimaryEmailUpdate}
 */
export function userProfilePrimaryEmailUpdate(
  userProfileId: number,
  newEmail: string
): IRDUserProfilePrimaryEmailUpdate {
  return {
    type: RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL,
    userProfileId,
    newEmail,
  };
}

/**
 * Action creator for USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS.
 * @returns {IRDUserProfilePrimaryEmailUpdateSuccess}
 */
export function userProfilePrimaryEmailUpdateSuccess(): IRDUserProfilePrimaryEmailUpdateSuccess {
  return {
    type: RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  };
}

/**
 * Action creator for USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL.
 * @returns {IRDUserProfilePrimaryEmailUpdateFail}
 */
export function userProfilePrimaryEmailUpdateFail(
  error: string
): IRDUserProfilePrimaryEmailUpdateFail {
  return {
    type: RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
    error,
  };
}

/**
 * Action creator for USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET.
 * @returns {IRDUserProfilePrimaryEmailUpdateReset}
 */
export function userProfilePrimaryEmailUpdateReset(): IRDUserProfilePrimaryEmailUpdateReset {
  return {
    type: RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL.
 * @param azureBenefit The program profile updates
 */
export function addAzureBenefitDetail(
  azureBenefit: IRDAzureBenefitClaim,
  userProfileId: number
): IRDAddAzureBenefitDetail {
  return {
    type: RD_ADD_AZURE_BENEFIT_DETAIL,
    azureBenefit,
    userProfileId,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL_SUCCESS.
 */
export function addAzureBenefitDetailSuccess(): IRDAddAzureBenefitDetailSuccess {
  return {
    type: RD_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  };
}

/**
 * Action creator for MVP_UPDATE_AZURE_BENEFIT_DETAIL_FAIL.
 * @param errorMessage The error message.
 */
export function addAzureBenefitDetailFail(errorMessage: string): IRDAddAzureBenefitDetailFail {
  return {
    type: RD_ADD_AZURE_BENEFIT_DETAIL_FAIL,
    errorMessage,
  };
}

export function addAzureBenefitReset(): IRDAddAzureBenefitReset {
  return {
    type: RD_ADD_AZURE_BENEFIT_RESET,
  };
}

export type RDUserProfileActionTypes =
  | IRDAddUserProfileCommentResetValues
  | IRDProgramProfileUpdateResetStatus
  | IRDUpdateUserProfileId
  | IRDUpdateProgramProfile
  | IRDUpdateProgramProfileSuccess
  | IRDUpdateProgramProfileFail
  | IRDAddUserProfileComment
  | IRDAddUserProfileCommentFail
  | IRDAddUserProfileCommentSuccess
  | IRDDownloadToCSV
  | IRDDownloadToCSVFail
  | IRDDownloadToCSVSuccess
  | IRDFetchUserProfile
  | IRDFetchUserProfileComments
  | IRDFetchUserProfileCommentsFail
  | IRDFetchUserProfileCommentsSuccess
  | IRDFetchUserProfileFail
  | IRDFetchUserProfileSuccess
  | IRDProfilesFail
  | IRDProfilesSearch
  | IRDProfilesSuccess
  | IRDUpdateCurrentPage
  | IRDUpdatePageSize
  | IRDResetFileDownloadStatus
  | IRDUserProfilesFilterProvinceValueRemove
  | IRDUserProfilesFilterValueAdd
  | IRDUserProfilesFilterValueRemove
  | IRDUserProfilesFilterValueUpdate
  | IRDClaimBenefit
  | IRDClaimBenefitSuccess
  | IRDClaimBenefitFail
  | IRDClaimBenefitReset
  | IRDGetProfileBenefits
  | IRDGetProfileBenefitsFail
  | IRDGetProfileBenefitsSuccess
  | IRDResetM365BenefitDetail
  | IRDFetchM365BenefitDetail
  | IRDFetchM365BenefitDetailFailure
  | IRDFetchM365BenefitDetailSuccess
  | IRDUpdateM365BenefitDetail
  | IRDUpdateM365BenefitDetailFailure
  | IRDUpdateM365BenefitDetailSuccess
  | IRDResetMS365BenefitDetail
  | IRDFetchMS365BenefitDetail
  | IRDFetchMS365BenefitDetailFailure
  | IRDFetchMS365BenefitDetailSuccess
  | IRDUpdateMS365BenefitDetail
  | IRDUpdateMS365BenefitDetailFailure
  | IRDUpdateMS365BenefitDetailSuccess
  | IRDFetchTeamsBenefitDetail
  | IRDFetchTeamsBenefitDetailFailure
  | IRDFetchTeamsBenefitDetailSuccess
  | IRDFetchProgramProfileSuccess
  | IRDFetchProgramProfileFail
  | IRDFetchProgramProfile
  | IRDExtendAzureBenefitCredit
  | IRDExtendAzureBenefitCreditSuccess
  | IRDExtendAzureBenefitCreditFailure
  | IRDExtendAzureBenefitCreditReset
  | IRDUserProfileIsPrivateUpdate
  | IRDUserProfileIsPrivateUpdateSuccess
  | IRDUserProfileIsPrivateUpdateFail
  | IRDUpdateSorting
  | IRDUserProfileFilterValueReset
  | IRDUserProfilePrimaryEmailUpdate
  | IRDUserProfilePrimaryEmailUpdateSuccess
  | IRDUserProfilePrimaryEmailUpdateFail
  | IRDUserProfilePrimaryEmailUpdateReset
  | IRDAddAzureBenefitDetail
  | IRDAddAzureBenefitDetailSuccess
  | IRDAddAzureBenefitDetailFail
  | IRDAddAzureBenefitReset;
